import { useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import PolygonIcon from '@mui/icons-material/PentagonOutlined';
import StraightenIcon from '@mui/icons-material/Straighten';
import { useAppDispatch, useAppSelector } from '../../app/hooks.js';
import { selectMeasureTool, selectMeasureVertices } from '../../store/selectors/index.js';
import {
  toolCleared,
  toolSelected,
  vertexRemoved,
  type MeasureTool,
} from '../../store/slices/measureSlice.js';

/**
 * The measurement tool control.
 *
 * It floats on the map rather than living in the side panel, because measuring
 * requires seeing what you are measuring - on a phone an open drawer covers the
 * map completely. It sits below the SDK's own zoom widget in the top-left
 * corner, and the results render in a separate panel opposite it.
 *
 * While a tool is active the map click belongs to the sketch, so identify is
 * suppressed. That is also why the two panels never collide: the identify panel
 * cannot be open while a measurement is being drawn.
 *
 * The strip selects a mode and nothing else. Clearing a measurement lives in
 * the results panel beside the point list, where what it applies to is
 * unambiguous. The existing OKMaps viewer offers "Measure length" and "Measure
 * area" as two toolbar buttons and has no separate clear control at all, so
 * this is also the shape its users already know.
 */
const TOOLS: ReadonlyArray<{
  id: MeasureTool;
  label: string;
  pressedLabel: string;
  icon: React.ReactElement;
}> = [
  {
    id: 'distance',
    // "Measure length" is what the existing viewer calls it; its users navigate
    // by muscle memory and there is no upside to renaming the same thing.
    label: 'Measure length',
    pressedLabel: 'Stop measuring length',
    icon: <StraightenIcon fontSize="small" />,
  },
  {
    id: 'area',
    label: 'Measure area',
    pressedLabel: 'Stop measuring area',
    icon: <PolygonIcon fontSize="small" />,
  },
];

export default function MeasureTools() {
  const dispatch = useAppDispatch();
  const tool = useAppSelector(selectMeasureTool);
  const vertices = useAppSelector(selectMeasureVertices);

  /**
   * Escape leaves the tool and Backspace removes the last point.
   *
   * The SDK's sketch already completes a shape on Enter or a double click.
   * These two are the ones it does not offer, and they are the ones a person
   * reaches for after a misplaced click. The listener is only attached while a
   * tool is active, so it cannot interfere with typing anywhere else.
   */
  useEffect(() => {
    if (!tool) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      // Never steal a key from a field the user is typing in.
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        dispatch(toolCleared());
        return;
      }
      if (event.key === 'Backspace' && vertices.length > 0) {
        event.preventDefault();
        dispatch(vertexRemoved(vertices.length - 1));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [tool, vertices.length, dispatch]);

  return (
    <Paper
      elevation={6}
      component="section"
      aria-label="Measurement tools"
      sx={{
        position: 'absolute',
        // Clear of the SDK's zoom widget, which sits at the top-left.
        top: 92,
        left: 15,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        zIndex: 2,
      }}
    >
      {TOOLS.map((t) => {
        const pressed = tool === t.id;
        return (
          <Tooltip key={t.id} title={pressed ? t.pressedLabel : t.label} placement="right">
            <IconButton
              size="small"
              aria-pressed={pressed}
              aria-label={pressed ? t.pressedLabel : t.label}
              onClick={() => dispatch(pressed ? toolCleared() : toolSelected(t.id))}
              sx={{
                borderRadius: 0,
                color: pressed ? 'primary.contrastText' : 'text.primary',
                bgcolor: pressed ? 'primary.main' : 'transparent',
                '&:hover': { bgcolor: pressed ? 'primary.dark' : 'action.hover' },
              }}
            >
              {t.icon}
            </IconButton>
          </Tooltip>
        );
      })}

    </Paper>
  );
}
